import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../../utils';

// Async thunks
export const fetchDeployed = createAsyncThunk(
    'deploy/packages',
    async () => {
        const response = await apiFetch('api/t/deployed');
        if (response.status !== 200) {
            throw new Error("Failed to fetch resources");
        }
        return await response.json();
    }
);

export const deleteDeployed = createAsyncThunk(
    'deploy/delete',
    async (payload) => {
        await apiFetch("api/d/" + payload, {
            method: 'DELETE'
        });
        return payload;
    }
)

export const fetchSelectedResources = createAsyncThunk(
    'deploy/resources/',
    async (payload, thunkAPI) => {
        const response = await apiFetch(
            "api/t/deployed/resources/" + payload,
            { signal: thunkAPI.signal }
        );

        if (response.status !== 200) {
            throw new Error("Failed to fetch resources");
        }
        return await response.json();
    }
);

export const extendDeployment = createAsyncThunk(
    "deploy/extend",
    async (deploymentId) => {
        const res = await apiFetch(`/api/t/deployed/extend/${deploymentId}`, {
            method: "POST",
            idempotencyAction: "deploy:extend"
        });

        return await res.json();
    }
);


export const resourceBackup = createAsyncThunk(
    "deploy/backup",
    async (resourceId) => {
        const res = await apiFetch(`/api/t/backup/${resourceId}`, {
            method: "POST",
            idempotencyAction: "deploy:backup"
        });

        return await res.json();
    }
);

export const applyDeplyomentConfig = createAsyncThunk(
    "configs/applyDeploymentConfig",
    async (payload) => {
        console.log("apply", payload)
        const response = await apiFetch(`api/c/d/${payload.id}`, {
            method: "POST",
            body: JSON.stringify(payload),
            idempotencyAction: "config:apply"
        });
        if (!response.ok) throw new Error("Deployment Config Application Failed");
        return await response.json();
    }
);

export const fetchDeploymentConfig = createAsyncThunk(
    "configs/fetchDeploymentConfig",
    async (id) => {
        const response = await apiFetch(`api/c/d/${id}`);
        return await response.json();
    }
);

const tenantDeploymentSlice = createSlice({
    name: 'tenantDeployment',
    initialState: {
        selectedPackage: null,
        selectedResources: null || [],
        packages: [],
        deploymentsLog: [],
        loading: true,
        error: false,
        activeConfig: []
    },
    reducers: {
        selectPackage: (state, action) => {
            const packageId = action.payload;
            if (packageId === null) {
                state.selectedPackage = null;
            }
            state.selectedPackage =
                state.packages.find(p => p.id === packageId) || null;
        },
        updateExpiration: (state, action) => {
            if (!state.selectedPackage) return;

            const selectedId = state.selectedPackage.id;

            const pkg = state.packages.find(p => p.id === selectedId);
            if (!pkg) return;

            const currentExpireAt = state.selectedPackage.expireAt;
            if (!currentExpireAt) return;

            const newExpireAt = new Date(
                new Date(currentExpireAt).getTime() + 60 * 60 * 1000
            );

            pkg.expireAt = newExpireAt;
            state.selectedPackage.expireAt = newExpireAt;
        },
        updateActiveConfigValue: (state, action) => {
            const { key, value } = action.payload;

            if (!state.activeConfig) {
                state.activeConfig = [];
            }

            const existing = state.activeConfig.find(
                x => x.key === key
            );

            if (existing) {
                existing.value = value;
            } else {
                state.activeConfig.push({
                    key,
                    value
                });
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDeployed.pending, (state) => {
                state.loading = true;
                state.error = false;
            })
            .addCase(fetchDeployed.fulfilled, (state, action) => {
                state.loading = false;
                state.packages = action.payload.deployments;
                state.deploymentsLog = action.payload.logs;
            })
            .addCase(fetchDeployed.rejected, (state, action) => {
                state.loading = false;
                state.error = true;
            })
            .addCase(deleteDeployed.pending, state => {
                state.loading = true;
                state.error = false;
            })
            .addCase(deleteDeployed.fulfilled, (state, action) => {
                state.loading = false;
                state.packages = state.packages.filter(p => p.id !== action.payload);
            })
            .addCase(deleteDeployed.rejected, state => {
                state.loading = false;
                state.error = true;
            })
            .addCase(fetchSelectedResources.pending, (state) => {
                state.loading = true;
                state.error = false;
                state.selectedResources = [];
            })
            .addCase(fetchSelectedResources.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedResources = action.payload;

                const targetPackage = state.packages.find(p => p.id === state.selectedPackage?.id);

                if (targetPackage) {
                    targetPackage.resources = action.payload;
                }
            })
            .addCase(fetchSelectedResources.rejected, state => {
                state.selectedResources = [];
                state.loading = false;
                state.error = true;
            })
            .addCase(extendDeployment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(extendDeployment.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(extendDeployment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(resourceBackup.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(resourceBackup.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(resourceBackup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(applyDeplyomentConfig.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(applyDeplyomentConfig.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(applyDeplyomentConfig.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(fetchDeploymentConfig.pending, (state, action) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDeploymentConfig.fulfilled, (state, action) => {
                state.loading = false;
                state.activeConfig = action.payload.fields;
            })
            .addCase(fetchDeploymentConfig.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                state.activeConfig = { id: null, fields: [] };
            });
    }
});
export const {
    selectPackage,
    updateExpiration,
    updateActiveConfigValue
} = tenantDeploymentSlice.actions;
export default tenantDeploymentSlice.reducer;
